import {useCallback, useEffect, useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {Button, Stack} from '@primer/react'
import {AgentIcon} from '@primer/octicons-react'
import {useMutation} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useAgenticAutofixJobPoll} from '@github-ui/code-scanning-shared/hooks/use-agentic-autofix-job-poll'
import {
  AgenticAutofixError,
  handleAgenticAutofixErrorResponse,
} from '@github-ui/code-scanning-shared/types/agentic-autofix-error'
import {displayFlashMessage} from '@github-ui/code-scanning-shared/utils/display-flash-message'
import {useWorkflowApprovalText} from './use-workflow-approval-text'

export type AgenticAutofixInitialState = 'unassigned' | 'pending' | 'completed' | 'failed'

export interface AssignToCopilotProps {
  url: string
  alertNumber: number
  commentId: number
  repository: {owner: string; name: string}
  initialState?: AgenticAutofixInitialState
  agenticAutofixJobId?: string
}

interface AssignToCopilotButtonProps extends AssignToCopilotProps {
  basePageDataUrl: string
}

type AssignToCopilotResponse = {agenticAutofixJobId?: string}

export function AssignToCopilotButton({
  basePageDataUrl,
  url,
  alertNumber,
  commentId,
  repository,
  initialState,
  agenticAutofixJobId,
}: AssignToCopilotButtonProps) {
  const [jobId, setJobId] = useState<string | null>(initialState === 'pending' ? (agenticAutofixJobId ?? null) : null)
  const completionHandledRef = useRef(false)

  const handleJobError = useCallback((message: string) => {
    displayFlashMessage('error', `Copilot failed to create a pull request. ${message}.`)
    setJobId(null)
  }, [])

  const {isComplete: polledJobIsComplete} = useAgenticAutofixJobPoll(
    repository.owner,
    repository.name,
    jobId,
    handleJobError,
    {
      completeOnPullRequest: false,
    },
  )

  const {mutate, isPending} = useMutation({
    mutationFn: async (): Promise<AssignToCopilotResponse> => {
      const response = await reactFetchJSON(url, {
        method: 'POST',
        body: {alert_number: alertNumber, comment_id: commentId},
      })
      if (!response.ok) {
        await handleAgenticAutofixErrorResponse(response)
      }
      return (await response.json()) as AssignToCopilotResponse
    },
    onSuccess: data => {
      if (data.agenticAutofixJobId) {
        setJobId(data.agenticAutofixJobId)
      }
    },
    onError: (error: Error) => {
      const message = error instanceof AgenticAutofixError ? error.message : 'Something went wrong, please try again.'
      displayFlashMessage('error', message)
    },
  })

  const isComplete = initialState === 'completed' || polledJobIsComplete
  const isWorking = isPending || (jobId !== null && !isComplete)
  const hasAssociatedJob = agenticAutofixJobId !== undefined || jobId !== null
  const workflowApprovalText = useWorkflowApprovalText({
    basePageDataUrl,
    enabled: isComplete && hasAssociatedJob,
  })
  const label = isComplete ? 'Copilot is done' : isWorking ? 'Copilot is working' : 'Assign to Copilot'

  useEffect(() => {
    if (!polledJobIsComplete || completionHandledRef.current) return

    completionHandledRef.current = true
    announce('Copilot is done')
  }, [polledJobIsComplete])

  useEffect(() => {
    if (workflowApprovalText) announce(workflowApprovalText)
  }, [workflowApprovalText])

  return (
    <Stack direction="horizontal" gap="condensed" align="center" justify="end" wrap="wrap">
      <Button
        type="button"
        size="small"
        leadingVisual={AgentIcon}
        loading={isWorking}
        loadingAnnouncement="Assigning to Copilot"
        aria-disabled={isWorking || isComplete}
        inactive={isWorking || isComplete}
        onClick={isWorking || isComplete ? undefined : () => mutate()}
        data-testid="assign-to-copilot-button"
      >
        {label}
      </Button>
      {workflowApprovalText && <span>{workflowApprovalText}</span>}
    </Stack>
  )
}
